.list {
  display: flex;
  flex-direction: column;
  gap: 28px;
}

/* Entry status summary card, mobile only - MobileClasses.dc.html's white-bg/orange-border card at
   the top of the Classes list ("You're entered in N classes" + paid/unpaid pills). Sits as the
   list's own first flex child so it shares the same 28px rhythm as the class-type groups below it,
   rather than needing its own margin. */
.statusCard {
  padding: 12px 14px;
  border-radius: var(--r-12);
  background: var(--bg-panel);
  border: 2px solid var(--accent);
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.statusHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 16px;
}

.statusIcon {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--accent);
  color: var(--text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
}

.statusPills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.statusPillPaid,
.statusPillUnpaid {
  font-size: 13px;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: var(--r-pill);
}
.statusPillPaid {
  background: var(--success-tint-bg);
  color: var(--success-text);
}
.statusPillUnpaid {
  background: var(--danger-tint-bg);
  color: var(--danger);
}

.statusHelper {
  font-size: 13px;
  color: var(--text-muted);
}

.group {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.groupLabel {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 24px;
  text-transform: uppercase;
  color: var(--accent-text);
}

.rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  background: var(--bg-panel);
  border-radius: var(--r-12);
  border: 1px solid var(--border-2);
}

/* Already-entered highlight, mobile - MobileClasses.dc.html gives a selected card a colored
   border (`border: 2px solid {{c.border}}`, `#ff6e00` when selected) rather than a tinted
   background. -1px padding compensates for the border growing from 1px to 2px, so the card's
   overall box size doesn't shift when it becomes highlighted. */
.rowEntered {
  border: 2px solid var(--accent);
  padding: 13px 15px;
}

/* Same treatment as .rowEntered above, but for a PAID entered class on mobile - green instead of
   orange, so a glance down the list tells "settled" apart from "still owes". Not in the mockup
   itself (MobileClasses.dc.html keeps every entered card orange regardless of paid state, only the
   badge inside is colored) - an explicit ask on top of it. */
.rowEnteredPaid {
  border: 2px solid var(--success);
  padding: 13px 15px;
}

/* Checkbox + name/rules/car-pill, always side by side (both breakpoints) - matches
   MobileClasses.dc.html's `display:flex; gap:6px; align-items:flex-start` and Desktop.dc.html's
   leading 56px checkbox column, just done with flex instead of a fixed-width grid. */
.rowBody {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-grow: 1;
  min-width: 0;
}

/* Bare checkbox cell, no visible label - Desktop.dc.html/MobileClasses.dc.html's
   `<label style="width:56px;height:44px;...">` around a lone `<input type="checkbox">`. */
.checkboxCell {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.checkboxCell input {
  width: 20px;
  height: 20px;
  accent-color: var(--accent);
  margin: 0;
}

/* Name + rules + car-pill/reason column. `min-width: 0` is required in both the flex (.rowBody)
   and grid (.rowGrid) contexts it sits in - without it, a flex/grid item won't shrink below its
   content's intrinsic width, so a long class name or rules paragraph would overflow into (or push
   out) the columns next to it instead of wrapping. */
.main {
  min-width: 0;
}

.nameLine {
  display: flex;
  flex-wrap: wrap;
  /* Was baseline - the name (17px) and the DISABLED pill (11px, its own padding box) have
     different baselines, so baseline alignment left the name looking slightly high relative to
     the pill next to it. Center keeps both vertically aligned as a pair regardless of their
     different font sizes. */
  align-items: center;
  gap: 10px;
}

.name {
  font-weight: 700;
  font-size: 17px;
}

.format {
  font-size: 14px;
  color: var(--text-muted);
}

/* Ported from events/displayEvent.cfm:663's real (non-dead) `badge bg-danger` DISABLED marker,
   next to the class name - a track has turned this class off, which otherwise reads as a blank
   row (no checkbox, no Sign up/Closed text, nothing) with no explanation at all. */
.disabledBadge {
  padding: 2px 8px;
  border-radius: var(--r-pill);
  background: var(--danger);
  color: var(--text-on-dark);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.rules {
  font-size: 13px;
  line-height: 1.4;
  padding: 8px 10px;
  border-radius: var(--r-8);
  background: var(--bg-page);
  color: var(--text-secondary);
}
/* sanitizeRichText() strips the pasted content's own inline margins along with its inline
   styles (see richText.ts) - a div/p has no default margin, so without this every line would
   run tight against the next with no break at all. */
.rules p,
.rules div {
  margin: 0 0 8px;
}
.rules p:last-child,
.rules div:last-child {
  margin-bottom: 0;
}

.meta {
  display: flex;
  align-items: center;
  gap: 14px;
}

.entered {
  font-size: 13px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}

.entriesLink {
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;
}

.action {
  margin-left: auto;
  font-size: 14px;
  font-weight: 700;
  padding: 6px 14px;
  border-radius: var(--r-pill);
  text-decoration: none;
}
.action.closed {
  background: var(--border-2);
  color: var(--text-muted);
}

/* The "Car" pill under an entered class's name - Desktop.dc.html/MobileClasses.dc.html's
   confirmed-car style (solid border; the mockup's dashed "needs a car" variant never applies
   here, since our flow never leaves a class checked without a vehicle already chosen). */
.carPill {
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  max-width: 100%;
  padding: 6px 10px;
  border-radius: var(--r-8);
  background: var(--bg-panel);
  border: 1.5px solid var(--border-1);
  color: var(--text-muted);
}

.carName {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 16px;
  color: var(--text-primary);
}

/* Same look as .carName, but an actual button - click to reopen the vehicle popup and swap cars
   (only rendered while the entry is unpaid). */
.carNameBtn {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 16px;
  color: var(--text-primary);
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  text-decoration-color: var(--border-4);
  text-underline-offset: 2px;
}
.carNameBtn:hover {
  color: var(--accent-text-hover);
}

.removeBtn {
  margin-left: auto;
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  border-radius: 50%;
  border: none;
  background: var(--border-2);
  color: var(--text-muted);
  font-size: 15px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}
.removeBtn:hover {
  background: var(--danger);
  color: #fff;
}

.confirmText {
  font-size: 13px;
  color: var(--text-secondary);
}

.confirmYes,
.confirmNo {
  margin-left: auto;
  height: 28px;
  padding: 0 10px;
  border-radius: var(--r-pill);
  font-size: 12px;
  font-weight: 700;
  border: none;
}
.confirmYes {
  background: var(--danger);
  color: #fff;
}
.confirmNo {
  margin-left: 0;
  background: var(--border-2);
  color: var(--text-primary);
}

.signupReason {
  margin-top: 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--accent-text);
}

.paidBadge {
  margin-left: 4px;
  padding: 2px 8px;
  border-radius: var(--r-pill);
  background: var(--success);
  color: var(--text-on-dark);
  font-size: 11px;
  font-weight: 700;
}

.entryPrice {
  margin-left: 4px;
  color: var(--text-muted);
  font-weight: 500;
  font-size: 14px;
}

/* Desktop only (ClassList renders this instead of .row/.rowBody/.meta when !isMobile) - a real
   CSS Grid with fixed-width outer columns, matching Desktop.dc.html's own
   `grid-template-columns: 56px 260px 1fr 110px 100px` table. Flexbox-with-independent-content-
   sizing (the original approach here) let each row's "entered"/"Entries" column land at a
   different width depending on that row's own text, so columns drifted out of alignment between
   rows - a real grid with the same template on every row fixes that by construction. The two
   inner columns use `fr` units (not the mockup's literal px) since this app's real class names run
   longer than the mockup's placeholders and need to flex rather than force awkward wraps. */
.rowGrid {
  display: grid;
  grid-template-columns: 32px minmax(160px, 1.4fr) minmax(120px, 1fr) 90px 90px;
  column-gap: 12px;
  /* Matches Desktop.dc.html's own grid row exactly (`align-items: center`) - this had drifted to
     `start`, which top-anchored every column to the row's tallest one (usually the 2-line format
     text), leaving the name/DISABLED pill pinned near the top of a taller row instead of centered
     in it. The formatCol/enteredCol `padding-top` nudges below existed only to patch that same
     `start` misalignment and are gone now that the row centers for real. */
  align-items: center;
  padding: 14px 16px;
  background: var(--bg-panel);
  border-radius: var(--r-12);
  border: 1px solid var(--border-2);
}

/* Already-entered highlight, desktop - was a tinted background (Desktop.dc.html's `background:
   {{c.bg}}`, `#fff7ef` when selected - this app's --bg-accent-tint); changed to the same
   border-only treatment as .rowEntered (mobile, above) and .extraCardEntered (add-ons,
   Lists.module.css) so classes and add-ons read as one consistent "entered" style. -1px padding
   offsets the border going from 1px to 2px so the row doesn't reflow when it becomes highlighted. */
.rowGridEntered {
  border: 2px solid var(--accent);
  padding: 13px 15px;
}

.formatCol {
  font-size: 15px;
  color: var(--text-secondary);
}

.enteredCol {
  font-size: 13px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.actionCol {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
}
.actionCol .action {
  margin-left: 0;
}
